import "./app.css";

import { BrowserRouter, NavLink, Route, Routes } from "react-router";

import { AuthProvider, useAuth } from "./auth";
import { ChangePasswordPage } from "./ChangePasswordPage";
import type { FeatureManifest } from "./feature";
import { LoginPage } from "./LoginPage";
import { PRODUCT_NAME } from "./product";
import { features as discoveredFeatures, navItems } from "./registry";

export { PRODUCT_NAME };

function Home() {
  return (
    <>
      <h1>Workbench</h1>
      <p>Choose an area from the navigation.</p>
    </>
  );
}

function NotPermitted() {
  return <p role="alert">You do not have permission to open this page.</p>;
}

function Shell({ features }: { features: FeatureManifest[] }) {
  const { state, logout, hasPermission } = useAuth();

  if (state.status === "loading") {
    return <p className="loading">Loading…</p>;
  }
  if (state.status === "anonymous") {
    return <LoginPage />;
  }
  if (state.user.must_change_password) {
    return <ChangePasswordPage />;
  }
  const allowed = (permissions: string[] | undefined) =>
    !permissions || permissions.length === 0 || permissions.some(hasPermission);
  const { user } = state;
  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">{PRODUCT_NAME}</span>
        <span className="who">
          Signed in as <strong>{user.display_name}</strong> ({user.roles.join(", ") || "no roles"})
        </span>
        <button type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </header>
      <div className="body">
        <nav aria-label="Primary" className="sidebar">
          <NavLink to="/" end>
            Home
          </NavLink>
          {navItems(features, allowed).map((item) => (
            <NavLink key={item.featureId} to={item.path} title={item.section}>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <main className="content">
          <Routes>
            <Route path="/" element={<Home />} />
            {features.flatMap((feature) =>
              feature.routes.map(({ path, Component }) => (
                <Route
                  key={path}
                  path={path}
                  element={allowed(feature.permissions) ? <Component /> : <NotPermitted />}
                />
              )),
            )}
            <Route path="*" element={<p>Page not found.</p>} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export function App({ features = discoveredFeatures }: { features?: FeatureManifest[] }) {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Shell features={features} />
      </AuthProvider>
    </BrowserRouter>
  );
}
